import { useState } from "react";

import styled from "styled-components";

import { TransactionComment } from "~/endpoints/transactions/types";

const Container = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;
  height: 100%;
  padding: 16px;
  background: #ffffff;
  box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.15);
  border-radius: 8px;
`;

const CommentsContainer = styled.section`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 10px;
  min-height: 0;
  overflow-y: scroll;
`;

const CommentContainer = styled.section`
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding-bottom: 10px;
  border-bottom: 1px solid #cfcfcf;
`;

const InputContainer = styled.section`
  display: flex;
  gap: 8px;
  padding: 15px 0 0 0;
`;

const WarningBlurb = styled.div`
  font-size: 13px;
  color: #a94442;
  background-color: #fef7f7;
  border-left: 4px solid #d9534f;
  padding: 8px 12px;
  margin-bottom: 8px;
  border-radius: 4px;
`;

export const datetime = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "numeric",
  timeZoneName: "short",
});
export const formatterDate = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "numeric",
});
export const formatterTime = new Intl.DateTimeFormat("en-US", {
  hour: "numeric",
  minute: "numeric",
});
export const formatterDateTime = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: true,
});

type TransactionCommentsProps = {
  transactionComments: TransactionComment[];
  onClickAddComment?: (commentText: string) => void;
};

const NoCommentsElement = () => (
  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", color: "#888" }}>
    No comments yet
  </div>
);

const CommentElement = (comment: TransactionComment) => (
  <CommentContainer>
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <strong>{comment.createdBy.userName}</strong>
      <div style={{ fontSize: "14px", color: "#4f4f4f" }}>{formatterDateTime.format(new Date(comment.createdAt))}</div>
    </div>
    <div>{comment.text}</div>
  </CommentContainer>
);

const CommentInput = ({
  input,
  setInput,
  handleAddComment,
}: {
  input: string;
  setInput: (value: string) => void;
  handleAddComment: () => void;
}) => (
  <input
    type="text"
    value={input}
    onChange={(e) => setInput(e.target.value)}
    placeholder="Add a comment..."
    style={{ flex: 1, padding: "8px" }}
    onKeyDown={(e) => {
      if (e.key === "Enter") handleAddComment();
    }}
  />
);

export default function TransactionComments({ transactionComments, onClickAddComment }: TransactionCommentsProps) {
  const [input, setInput] = useState("");

  const handleAddComment = () => {
    if (input.trim() && onClickAddComment) {
      onClickAddComment(input.trim());
      setInput("");
    }
  };

  return (
    <Container>
      <CommentsContainer>
        {transactionComments.length === 0 ? (
          <NoCommentsElement />
        ) : (
          transactionComments.map((comment, index) => <CommentElement key={index} {...comment} />)
        )}
      </CommentsContainer>
      <InputContainer>
        <CommentInput input={input} setInput={setInput} handleAddComment={handleAddComment} />
        <button onClick={handleAddComment} disabled={!input.trim()}>
          Add Comment
        </button>
      </InputContainer>
      <WarningBlurb>
        Warning: Once submitted, comments cannot be edited or deleted. Please double-check before posting.
      </WarningBlurb>
    </Container>
  );
}
